import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Breadcrumbs,
  Link,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import {
  Science,
  TrendingUp,
  Psychology,
  Public,
  Business,
  Email,
  ChevronRight,
  Bolt,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';
import { newsData } from '../data/newsData';

const IconMap = {
  Science,
  TrendingUp,
  Psychology,
  Public,
  Business,
  Email,
  Bolt
};

// Slide images are dense, so each figure links to the full-size file
const NewsFigure = ({ image, sx = {} }) => (
  <Box component="figure" sx={{ m: 0, ...sx }}>
    <a href={image.src} target="_blank" rel="noopener noreferrer">
      <Box
        component="img"
        src={image.src}
        alt={image.alt}
        loading="lazy"
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          cursor: 'zoom-in'
        }}
      />
    </a>
    {image.caption && (
      <Typography component="figcaption" variant="body2" sx={{ mt: 1.5, color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>
        {image.caption}
      </Typography>
    )}
  </Box>
);

const NewsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Sort news by publishDate (latest first)
  const sortedNews = [...newsData].sort((a, b) =>
    new Date(b.publishDate) - new Date(a.publishDate)
  );

  const currentNews = newsData.find(n => n.slug === slug);

  // Helper to render icon
  const renderIcon = (iconName, props = {}) => {
    const IconComponent = IconMap[iconName];
    return IconComponent ? <IconComponent {...props} /> : null;
  };

  // --- LIST VIEW ---
  if (!slug) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 8, pb: 12 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'rgb(20, 50, 90)', mb: 2 }}>
              Genion News
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '700px', mx: 'auto', fontWeight: 400 }}>
              Stay updated with our latest scientific breakthroughs, product announcements, and company milestones.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {sortedNews.map((news) => (
              <Grid item xs={12} key={news.id}>
                <Card sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 40px rgba(49, 110, 177, 0.12)'
                  }
                }}>
                  {news.images?.[0] && (
                    <CardMedia
                      component="img"
                      image={news.images[0]}
                      alt=""
                      sx={{
                        width: { xs: '100%', md: 360 },
                        flexShrink: 0,
                        aspectRatio: '3 / 2',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/news/${news.slug}`)}
                    />
                  )}
                  <CardContent sx={{ flex: 1, p: 4 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={news.tag}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(49, 110, 177, 0.1)',
                          color: 'rgb(49, 110, 177)',
                          fontWeight: 700,
                          fontSize: '0.7rem'
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {news.date}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'rgb(20, 50, 90)', mb: 2, fontSize: '1.75rem' }}>
                      {news.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#475569', mb: 3, lineHeight: 1.6 }}>
                      {news.summary}
                    </Typography>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => navigate(`/news/${news.slug}`)}
                      endIcon={<ArrowForward />}
                      sx={{ fontWeight: 700, p: 0, '&:hover': { background: 'none', textDecoration: 'underline' } }}
                    >
                      See Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // --- DETAIL VIEW ---
  if (!currentNews) {
    return (
      <Container sx={{ py: 20, textAlign: 'center' }}>
        <Typography variant="h4">News item not found.</Typography>
        <Button sx={{ mt: 4 }} onClick={() => navigate('/news')}>Back to News</Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', pt: 8, pb: 12 }}>
      <Container maxWidth="md">
        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Breadcrumbs separator={<ChevronRight fontSize="small" />} aria-label="breadcrumb">
            <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
              Home
            </Link>
            <Link underline="hover" color="inherit" onClick={() => navigate('/news')} sx={{ cursor: 'pointer' }}>
              News
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>Details</Typography>
          </Breadcrumbs>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/news')}
            sx={{ fontWeight: 600, color: 'text.secondary' }}
          >
            All News
          </Button>
        </Box>

        <Paper elevation={0} sx={{
          p: { xs: 4, md: 8 },
          borderRadius: 4,
          boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0'
        }}>
          {/* Article Header */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Chip
                label={currentNews.tag}
                sx={{
                  backgroundColor: 'rgba(49, 110, 177, 0.1)',
                  color: 'rgb(49, 110, 177)',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderRadius: '6px'
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {currentNews.date}
              </Typography>
            </Box>

            <Typography variant="h3" component="h1" sx={{
              fontWeight: 800,
              color: 'rgb(20, 50, 90)',
              lineHeight: 1.2,
              mb: 3,
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}>
              {currentNews.title}
            </Typography>

            {currentNews.location && (
              <Typography variant="h6" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2, fontWeight: 400 }}>
                {currentNews.location}
              </Typography>
            )}

            {currentNews.heroImage && (
              <NewsFigure image={currentNews.heroImage} sx={{ mt: 4 }} />
            )}
          </Box>

          <Divider sx={{ mb: 6 }} />

          {/* Lead Sections */}
          {currentNews.lead.map((paragraph, idx) => (
            <Typography key={idx} variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8, mb: 4, color: '#334155' }}>
              {paragraph}
            </Typography>
          ))}

          {/* Dynamic Sections */}
          {currentNews.sections.map((section, idx) => (
            <Box key={idx} sx={{
              mb: 6,
              ...(section.isHighlight && {
                bgcolor: 'rgba(78, 158, 163, 0.03)',
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: '1px solid rgba(78, 158, 163, 0.1)'
              })
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {renderIcon(section.icon, { sx: { mr: 1.5, color: 'rgb(49, 110, 177)' } })}
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgb(20, 50, 90)' }}>
                  {section.title}
                </Typography>
              </Box>

              {section.content && section.content.map((p, pIdx) => (
                <Typography key={pIdx} variant="body1" sx={{ lineHeight: 1.8, color: '#475569', mb: 3 }}>
                  {p}
                </Typography>
              ))}

              {section.list && (
                <Box sx={{ mb: 3 }}>
                  <ul style={{ paddingLeft: '20px', color: '#475569', lineHeight: '1.8' }}>
                    {section.list.map((item, lIdx) => (
                      <li key={lIdx} style={{ marginBottom: '8px' }}>{item}</li>
                    ))}
                  </ul>
                </Box>
              )}

              {section.items && (
                <List sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', mb: 2 } }}>
                  {section.items.map((item, iIdx) => (
                    <ListItem key={iIdx} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        <Bolt sx={{ color: 'rgb(78, 158, 163)', fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.primary}
                        secondary={item.secondary}
                        primaryTypographyProps={{ fontWeight: 700, color: '#1e293b' }}
                        secondaryTypographyProps={{ sx: { mt: 0.5, lineHeight: 1.6 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {section.footer && (
                <Typography sx={{ mt: 2, fontStyle: 'italic', color: '#64748b' }}>
                  {section.footer}
                </Typography>
              )}

              {section.image && (
                <NewsFigure image={section.image} sx={{ mt: 4 }} />
              )}
            </Box>
          ))}

          {/* Impact Section */}
          {currentNews.impact && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {renderIcon(currentNews.impact.icon, { sx: { mr: 1.5, color: 'rgb(49, 110, 177)' } })}
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgb(20, 50, 90)' }}>
                  {currentNews.impact.title}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#475569', mb: 2 }}>
                {currentNews.impact.content}
              </Typography>
              <ul style={{ paddingLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
                {currentNews.impact.list.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#475569' }}>
                {currentNews.impact.footer}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default NewsPage;